import React, { useState, useEffect } from "react";
import { db, storage } from "../../services/firebase";
import {
	collection,
	onSnapshot,
	doc,
	addDoc,
	updateDoc,
	deleteDoc,
	query,
	orderBy,
} from "firebase/firestore";
import {
	ref,
	uploadBytes,
	getDownloadURL,
	deleteObject,
} from "firebase/storage";
import { Plus, Edit3, Trash2, Upload, X, Save } from "lucide-react";

const MAX_IMAGE_SIZE = 500 * 1024; // 500KB

const SIZES = ["XS", "S", "M", "L", "XL", "2XL", "One Size"];

const MerchProductManager = () => {
	const [products, setProducts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showForm, setShowForm] = useState(false);
	const [editingProduct, setEditingProduct] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [imagePreview, setImagePreview] = useState(null);
	const [imageFile, setImageFile] = useState(null);
	const [error, setError] = useState("");

	const [formData, setFormData] = useState({
		name: "",
		description: "",
		price: "",
		sizes: [...SIZES],
	});

	useEffect(() => {
		const q = query(
			collection(db, "merchProducts"),
			orderBy("createdAt", "desc"),
		);
		const unsubscribe = onSnapshot(q, (snapshot) => {
			const productsData = snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
			setProducts(productsData);
			setLoading(false);
		});
		return () => unsubscribe();
	}, []);

	// Existing products that were previously hardcoded
	const LEGACY_PRODUCTS = [
		{
			name: "Relax Crew - Ecru Cream",
			image: "/Relax Crew Ecru Cream.jpg",
			price: 50,
		},
		{
			name: "Relax Crew - Shadow",
			image: "/Relax Crew Shadow.jpg",
			price: 50,
		},
		{
			name: "Stella Mia Vest - Fraiche Peche",
			image: "/Stella Mia Vest Fraiche Peche.jpg",
			price: 25,
		},
		{
			name: "Unisex Anthem Tee - Black",
			image: "/Unisex Anthem Tee Black.jpg",
			price: 30,
		},
		{
			name: "Womens Stella Boxy Tee - Natural",
			image: "/Womens Stella Boxy Tee Natural.jpg",
			price: 30,
		},
		{
			name: "Womens Stella Nova Boxy Tee - Black",
			image: "/Womens Stella Nova Boxy tee black.jpg",
			price: 30,
		},
	];

	const [seeding, setSeeding] = useState(false);

	const seedExistingProducts = async () => {
		if (
			!window.confirm(
				"This will add the 6 original merch products to your products list. Continue?",
			)
		) {
			return;
		}

		setSeeding(true);
		try {
			const baseUrl = window.location.origin;
			for (const item of LEGACY_PRODUCTS) {
				await addDoc(collection(db, "merchProducts"), {
					name: item.name,
					description: "",
					price: item.price,
					sizes: [...SIZES],
					imageUrl: baseUrl + item.image,
					imagePath: "", // Static file, not in Firebase Storage
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString(),
				});
			}
		} catch (err) {
			console.error("Error seeding products:", err);
			setError("Failed to seed products: " + err.message);
		} finally {
			setSeeding(false);
		}
	};

	const resetForm = () => {
		setFormData({ name: "", description: "", price: "", sizes: [...SIZES] });
		setImageFile(null);
		setImagePreview(null);
		setEditingProduct(null);
		setShowForm(false);
		setError("");
	};

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		if (file.size > MAX_IMAGE_SIZE) {
			setError(
				`Image must be under 500KB. Selected file is ${(file.size / 1024).toFixed(0)}KB.`,
			);
			e.target.value = "";
			return;
		}

		if (!file.type.startsWith("image/")) {
			setError("Please select a valid image file.");
			e.target.value = "";
			return;
		}

		setError("");
		setImageFile(file);
		setImagePreview(URL.createObjectURL(file));
	};

	const toggleSize = (size) => {
		setFormData((prev) => ({
			...prev,
			sizes: prev.sizes.includes(size)
				? prev.sizes.filter((s) => s !== size)
				: [...prev.sizes, size],
		}));
	};

	const startEdit = (product) => {
		setEditingProduct(product);
		setFormData({
			name: product.name,
			description: product.description || "",
			price: product.price.toString(),
			sizes: product.sizes || [...SIZES],
		});
		setImagePreview(product.imageUrl || null);
		setImageFile(null);
		setShowForm(true);
		setError("");
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!formData.name || !formData.price) {
			setError("Name and price are required.");
			return;
		}

		if (!editingProduct && !imageFile) {
			setError("Please upload a product image.");
			return;
		}

		if (formData.sizes.length === 0) {
			setError("Please select at least one available size.");
			return;
		}

		setSubmitting(true);
		setError("");

		try {
			let imageUrl = editingProduct?.imageUrl || "";
			let imagePath = editingProduct?.imagePath || "";

			// Upload new image if provided
			if (imageFile) {
				const fileName = `merch/${Date.now()}-${imageFile.name}`;
				const storageRef = ref(storage, fileName);
				await uploadBytes(storageRef, imageFile);
				imageUrl = await getDownloadURL(storageRef);

				// Delete old image if replacing
				if (editingProduct?.imagePath) {
					try {
						const oldRef = ref(storage, editingProduct.imagePath);
						await deleteObject(oldRef);
					} catch (err) {
						// Old image may not exist, that's fine
						console.warn("Could not delete old image:", err);
					}
				}

				imagePath = fileName;
			}

			const productData = {
				name: formData.name,
				description: formData.description,
				price: parseFloat(formData.price),
				sizes: formData.sizes,
				imageUrl,
				imagePath,
				updatedAt: new Date().toISOString(),
			};

			if (editingProduct) {
				await updateDoc(
					doc(db, "merchProducts", editingProduct.id),
					productData,
				);
			} else {
				productData.createdAt = new Date().toISOString();
				await addDoc(collection(db, "merchProducts"), productData);
			}

			resetForm();
		} catch (err) {
			console.error("Error saving product:", err);
			setError("Failed to save product. " + err.message);
		} finally {
			setSubmitting(false);
		}
	};

	const handleDelete = async (product) => {
		if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) {
			return;
		}

		try {
			// Delete image from storage
			if (product.imagePath) {
				try {
					const imageRef = ref(storage, product.imagePath);
					await deleteObject(imageRef);
				} catch (err) {
					console.warn("Could not delete image:", err);
				}
			}

			await deleteDoc(doc(db, "merchProducts", product.id));
		} catch (err) {
			console.error("Error deleting product:", err);
			setError("Failed to delete product.");
		}
	};

	if (loading) {
		return (
			<div className="text-center py-4">
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Loading...</span>
				</div>
			</div>
		);
	}

	return (
		<div>
			<div className="d-flex justify-content-between align-items-center mb-3">
				<h6 className="mb-0">Products ({products.length})</h6>
				{!showForm && (
					<button
						className="btn btn-primary btn-sm d-flex align-items-center gap-1"
						onClick={() => {
							resetForm();
							setShowForm(true);
						}}
					>
						<Plus size={16} />
						Add Product
					</button>
				)}
			</div>

			{/* Add/Edit Form */}
			{showForm && (
				<div className="p-3 bg-light rounded mb-4 workshop-form-mobile">
					<div className="d-flex justify-content-between align-items-center mb-3">
						<h6 className="mb-0" style={{ fontSize: "0.95rem" }}>
							{editingProduct ? "Edit Product" : "Add New Product"}
						</h6>
						<button
							className="btn btn-sm btn-outline-secondary"
							onClick={resetForm}
						>
							<X size={16} />
						</button>
					</div>

					{error && (
						<div className="alert alert-danger py-2 small">{error}</div>
					)}

					<form onSubmit={handleSubmit}>
						<div className="row g-3">
							<div className="col-12 col-md-6">
								<label className="form-label">Product Name *</label>
								<input
									type="text"
									className="form-control"
									value={formData.name}
									onChange={(e) =>
										setFormData({ ...formData, name: e.target.value })
									}
									placeholder="e.g. Relax Crew - Ecru Cream"
									required
								/>
							</div>
							<div className="col-12 col-md-6">
								<label className="form-label">Price (£) *</label>
								<input
									type="number"
									step="0.01"
									min="0"
									className="form-control"
									value={formData.price}
									onChange={(e) =>
										setFormData({ ...formData, price: e.target.value })
									}
									placeholder="e.g. 30.00"
									required
								/>
							</div>
							<div className="col-12">
								<label className="form-label">Description</label>
								<textarea
									className="form-control"
									rows="2"
									value={formData.description}
									onChange={(e) =>
										setFormData({ ...formData, description: e.target.value })
									}
									placeholder="Optional product description"
								/>
							</div>
							<div className="col-12">
								<label className="form-label">Available Sizes</label>
								<div className="d-flex flex-wrap gap-2">
									{SIZES.map((size) => (
										<button
											key={size}
											type="button"
											className="btn btn-sm"
											onClick={() => toggleSize(size)}
											style={{
												backgroundColor: formData.sizes.includes(size)
													? "#d15a36"
													: "transparent",
												color: formData.sizes.includes(size)
													? "#fff"
													: "rgba(255,255,255,0.4)",
												border: formData.sizes.includes(size)
													? "2px solid #d15a36"
													: "2px solid #555",
												fontWeight: formData.sizes.includes(size)
													? "600"
													: "400",
												minWidth: size === "One Size" ? "80px" : "42px",
											}}
										>
											{size}
										</button>
									))}
								</div>
								<small className="text-muted d-block mt-1">
									Click to toggle sizes on/off
								</small>
							</div>
							<div className="col-12">
								<label className="form-label">
									Product Image *{" "}
									<small className="text-muted">(max 500KB)</small>
								</label>
								<input
									type="file"
									className="form-control"
									accept="image/*"
									onChange={handleImageChange}
								/>
								{imagePreview && (
									<div className="mt-2">
										<img
											src={imagePreview}
											alt="Preview"
											style={{
												maxWidth: "150px",
												maxHeight: "150px",
												objectFit: "cover",
												borderRadius: "8px",
											}}
										/>
									</div>
								)}
							</div>
							<div className="col-12">
								<div className="d-flex flex-column flex-sm-row gap-2">
									<button
										type="submit"
										className="btn btn-success d-flex align-items-center justify-content-center gap-1 flex-grow-1 flex-sm-grow-0"
										disabled={submitting}
									>
										{submitting ? (
											<>
												<span
													className="spinner-border spinner-border-sm"
													role="status"
												/>
												Saving...
											</>
										) : (
											<>
												<Save size={16} />
												{editingProduct ? "Update Product" : "Add Product"}
											</>
										)}
									</button>
									<button
										type="button"
										className="btn btn-secondary flex-grow-1 flex-sm-grow-0"
										onClick={resetForm}
										disabled={submitting}
									>
										Cancel
									</button>
								</div>
							</div>
						</div>
					</form>
				</div>
			)}

			{/* Products List */}
			{products.length === 0 && !showForm ? (
				<div className="text-center py-5">
					<p className="text-muted">
						No products added yet. Add your first product or import the existing
						ones.
					</p>
					<button
						className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 mx-auto"
						onClick={seedExistingProducts}
						disabled={seeding}
					>
						{seeding ? (
							<>
								<span
									className="spinner-border spinner-border-sm"
									role="status"
								/>
								Importing...
							</>
						) : (
							<>
								<Upload size={16} />
								Import Existing Products
							</>
						)}
					</button>
				</div>
			) : (
				<div className="row g-3">
					{products.map((product) => (
						<div key={product.id} className="col-12 col-sm-6 col-lg-4">
							<div
								className="card h-100"
								style={{ backgroundColor: "#2a2a2a", border: "1px solid #333" }}
							>
								{product.imageUrl && (
									<img
										src={product.imageUrl}
										alt={product.name}
										className="card-img-top"
										style={{
											height: "180px",
											objectFit: "cover",
										}}
									/>
								)}
								<div className="card-body d-flex flex-column p-2 p-sm-3">
									<h6
										className="card-title mb-1"
										style={{ color: "#fff", fontSize: "0.9rem" }}
									>
										{product.name}
									</h6>
									{product.description && (
										<p
											className="card-text small mb-2"
											style={{
												color: "rgba(255,255,255,0.6)",
												fontSize: "0.8rem",
											}}
										>
											{product.description}
										</p>
									)}
									<p className="fw-bold mb-2" style={{ color: "#d15a36" }}>
										£{product.price.toFixed(2)}
									</p>
									<div className="d-flex flex-wrap gap-1 mb-2">
										{(product.sizes || []).map((s) => (
											<span
												key={s}
												className="badge bg-secondary"
												style={{ fontSize: "0.65rem" }}
											>
												{s}
											</span>
										))}
									</div>
									<div className="mt-auto d-flex gap-2">
										<button
											className="btn btn-sm btn-outline-primary flex-grow-1"
											onClick={() => startEdit(product)}
										>
											<Edit3 size={14} className="me-1" />
											Edit
										</button>
										<button
											className="btn btn-sm btn-outline-danger"
											onClick={() => handleDelete(product)}
										>
											<Trash2 size={14} />
										</button>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default MerchProductManager;
